import React, { createContext, useReducer, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import produce, { enableES5 } from 'immer';
import dynamic from 'next/dynamic';
import { reducerWithLogger } from './util';
import collections from './collections';
import track from './track';
import AccountContext from './AccountContext';

// Enable ES5 support for immer
enableES5();

const CollectionsContext = createContext({});

const SESSION_KEY = 'collections:state';

const INIT_COLLECTIONS = 'COLLECTIONS:INIT';
const CLOSE_COLLECTIONS = 'COLLECTIONS:CLOSE';
const UNSET_COLLECTIONS = 'COLLECTIONS:UNSET';
const CREATE_COLLECTION = 'COLLECTIONS:CREATE';
const ADD_TO_COLLECTION = 'COLLECTIONS:ADD_TO';
const REMOVE_FROM_COLLECTION = 'COLLECTIONS:REMOVE_FROM';
const SELECT_COLLECTION = 'COLLECTIONS:SELECT_COLLECTION';
const DESELECT_COLLECTION = 'COLLECTIONS:DESELECT_COLLECTION';
const SET_SELECTED_CONTENT = 'COLLECTIONS:SET_CONTENT';
const UNSET_SELECTED_CONTENT = 'COLLECTIONS:UNSET_CONTENT';

const SET_MODAL_IS_OPEN = 'COLLECTIONS:SET_MODAL_IS_OPEN';
const SET_RIBBON_EXPANDED = 'COLLECTIONS:RIBBON_EXPANDED';
const SET_RIBBON_IS_SHOWN = 'COLLECTIONS:RIBBON_IS_SHOWN';

const maxItems = 50;

const CollectionsModal = dynamic(
  () => import('../components/Collections/CollectionsModal'),
  { ssr: false }
);

const CollectionsRibbon = dynamic(
  () => import('../components/Collections/CollectionsRibbon'),
  { ssr: false }
);

const initialState = {
  ready: false,
  collections: {},
  currentCollectionId: null,
  contentModels: {},
  selectedContent: null,
  modalIsOpen: false,
  ribbonExpanded: false,
  ribbonIsShown: false,
};

let firedCollectionsModalEvent = false;

const reducer = reducerWithLogger(
  produce((state, action) => {
    const collectionsContentModelKey =
      `${action.contentType}|${action.contentId}` || '';

    switch (action.type) {
      case CREATE_COLLECTION:
        state.collections[action.collection.id] = action.collection;
        state.currentCollectionId = action.collection.id;
        state.ribbonExpanded = true;
        return;

      case ADD_TO_COLLECTION: {
        let contents = action.contents;
        for (let i = 0; i < contents.length; i++) {
          let cmKey = contents[i].type + '|' + contents[i].id;
          state.contentModels[cmKey] = contents[i];
          state.collections[action.collectionId].content[cmKey] = {
            type: contents[i].type,
            id: contents[i].id,
          };
        }
        state.ribbonIsShown = true;
        return;
      }

      case REMOVE_FROM_COLLECTION:
        // Remove from the collection
        delete state.collections[action.collectionId].content[
          collectionsContentModelKey
        ];
        // If content is no longer in any collection, remove from contentModels
        if (
          !Object.values(state.collections).filter(c => {
            return c.content && collectionsContentModelKey in c.content;
          }).length
        ) {
          delete state.contentModels[collectionsContentModelKey];
        }
        return;

      case INIT_COLLECTIONS:
        state.ready = true;
        state.collections = action.collections;
        state.contentModels = action.contentModels;
        Object.entries(action.state).forEach(
          ([key, value]) => (state[key] = value)
        );
        return;

      case UNSET_COLLECTIONS:
        return {
          ...initialState,
          ready: true,
        };

      case SELECT_COLLECTION:
        state.currentCollectionId = action.collectionId;
        state.ribbonExpanded = true;
        state.ribbonIsShown = true;
        return;

      case DESELECT_COLLECTION:
        state.currentCollectionId = '';
        state.ribbonIsShown = false;
        return;

      case SET_SELECTED_CONTENT:
        state.selectedContent = action.contents;
        return;

      case UNSET_SELECTED_CONTENT:
        state.selectedContent = null;
        return;

      case SET_MODAL_IS_OPEN:
        state.modalIsOpen = action.open;
        return;

      case SET_RIBBON_EXPANDED:
        state.ribbonExpanded = action.expanded;
        return;

      case SET_RIBBON_IS_SHOWN:
        state.ribbonIsShown = action.open;
        return;

      case CLOSE_COLLECTIONS:
        state.ribbonExpanded = false;
        state.ribbonIsShown = false;
        state.selectedContent = null;
        state.currentCollectionId = null;
        return;
    }
  })
);

const CollectionsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const init = async () => {
    const prevState = getSessionState();
    const data = await collections.getAll();
    dispatch({
      type: INIT_COLLECTIONS,
      ...data,
      // Only restore a valid previous state
      state: prevState.currentCollectionId in data.collections ? prevState : {},
    });
    return data;
  };

  const createCollection = async title => {
    const collection = await collections.create(title);
    dispatch({ type: CREATE_COLLECTION, collection });
    return collection;
  };

  const addToCollection = async (contents, collectionId) => {
    let newContentCount = 0;
    for (let i = 0; i < contents.length; i++) {
      if (!isCollected(contents[i].type, contents[i].id, collectionId)) {
        newContentCount += 1;
      }
    }
    if (!newContentCount) {
      alert('All contents you selected are already in the collection!');
      return;
    }

    if (state.collections?.[collectionId]?.content &&
            Object.keys(state.collections[collectionId].content).length + newContentCount > maxItems) {
      alert('Adding items will exceed the max of ' + maxItems + ' items.');
      return;
    }

    await collections.addTo(contents, collectionId);
    dispatch({
      type: ADD_TO_COLLECTION,
      contents,
      collectionId,
    });
  };

  const removeFromCollection = async (contentType, contentId, collectionId) => {
    await collections.removeFrom(contentType, contentId, collectionId);
    dispatch({
      type: REMOVE_FROM_COLLECTION,
      contentType,
      contentId,
      collectionId,
    });
  };

  const setCurrentCollectionId = collectionId => {
    dispatch({ type: SELECT_COLLECTION, collectionId });
  };

  const setSelectedContent = contents => {
    dispatch({
      type: SET_SELECTED_CONTENT,
      contents,
    });
  };

  const unsetSelectedContent = () => {
    dispatch({ type: UNSET_SELECTED_CONTENT });
  };

  const setModalIsOpen = open => {
    dispatch({ type: SET_MODAL_IS_OPEN, open });
  };

  const setRibbonExpanded = expanded => {
    dispatch({ type: SET_RIBBON_EXPANDED, expanded });
  };

  const closeCollections = () => {
    dispatch({ type: CLOSE_COLLECTIONS });
  };

  /**
   * Check if the given content item has been collected. If a collectionId is provided,
   * only that collection will be checked.
   */
  const isCollected = (contentType, contentId, collectionId = null) => {
    if (!state.contentModels) {
      return false;
    }

    const key = `${contentType}|${contentId}`;

    if (collectionId) {
      return (
        collectionId in state.collections &&
        key in state.collections[collectionId].content
      );
    } else {
      return key in state.contentModels;
    }
  };

  const trackFeatureWall = () => {
    if (!firedCollectionsModalEvent) {
      firedCollectionsModalEvent = true;
      track('collections', { action: 'hit feature wall' });
    }
  };

  const collectContent = async (type, id, model, collectionId = null) => {
    if (!collectionId && !state.currentCollectionId) {
      model['flag'] = 'collectSingle';
      setSelectedContent([model]);
      setModalIsOpen(true);
      return;
    }

    if (!isCollected(type, id, collectionId ?? state.currentCollectionId)) {
      await addToCollection([model], collectionId ?? state.currentCollectionId);
    } else {
      await removeFromCollection(
        type,
        id,
        collectionId ?? state.currentCollectionId
      );
    }

    unsetSelectedContent();
  };

  const unsetCollections = () => {
    dispatch({ type: UNSET_COLLECTIONS });
  };

  const setSessionState = () => {
    if (typeof window === 'undefined') return;

    const { currentCollectionId, ribbonIsShown, ribbonExpanded } = state;
    try {
      window.sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({
          currentCollectionId,
          ribbonIsShown,
          ribbonExpanded,
        })
      );
    } catch {
      // Ignore sessionStorage errors
    }
  };

  const getSessionState = () => {
    if (typeof window === 'undefined') return;
    try {
      return JSON.parse(window.sessionStorage.getItem(SESSION_KEY)) ?? {};
    } catch {
      // Ignore sessionStorage errors
    }
  };

  const account = useContext(AccountContext);

  useEffect(() => {
    if (account.ready) {
      if (account.memberId) {
        init().catch(error => console.error(error)); // Logged in, init collections
      } else {
        unsetCollections(); // Not logged in, go back to initial state
      }
    }
  }, [account.ready, account.memberId]);

  useEffect(() => {
    if (state.ready) setSessionState();
  }, [state]);

  const value = {
    ...state,
    isCollected,
    createCollection,
    setModalIsOpen,
    setRibbonExpanded,
    closeCollections,
    setCurrentCollectionId,
    trackFeatureWall,
    collectContent,
    removeFromCollection,
    addToCollection,
    setSelectedContent,
  };

  return (
    <CollectionsContext.Provider value={value}>
      {children}
      <CollectionsModal />
      {state.ribbonIsShown && state.currentCollectionId && (
        <CollectionsRibbon />
      )}
    </CollectionsContext.Provider>
  );
};

CollectionsProvider.displayName = 'CollectionsProvider';
CollectionsProvider.propTypes = {
  children: PropTypes.node,
};

export { CollectionsContext as default, CollectionsProvider };
